import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, TextField, Typography, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockIcon from "@mui/icons-material/Lock";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import useMediaQuery from "@mui/material/useMediaQuery";
import theme from "../../../../../Layouts/theme";
import sh from "bundles/common/utils/sh";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import ConfirmModal from "bundles/common/components/ConfirmModal";
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { attachClosestEdge, extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";

const MAX_COLUMNS = 7;
const protectedKeys = new Set(["pending", "hired"]);
const sortColumnsByPosition = (cols) => [...(cols || [])].sort((a, b) => (a.position || 0) - (b.position || 0));

const DRAG_TYPE = "manage-board-column";
const DROP_TYPE = "manage-board-column-drop-target";

const DraggableColumnRow = ({ column, isDraggable, isDragging, dropIndicatorEdge, children }) => {
  const rowRef = useRef(null);
  const handleRef = useRef(null);

  useEffect(() => {
    const el = rowRef.current;
    const handleEl = handleRef.current;
    if (!el) return undefined;

    const cleanups = [];

    cleanups.push(
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) => source?.data?.type === DRAG_TYPE,
        getData: ({ input, element }) =>
          attachClosestEdge(
            { type: DROP_TYPE, columnId: Number(column.id) },
            { input, element, allowedEdges: ["top", "bottom"] }
          ),
      })
    );

    if (isDraggable && handleEl) {
      cleanups.push(
        draggable({
          element: el,
          dragHandle: handleEl,
          getInitialData: () => ({ type: DRAG_TYPE, columnId: Number(column.id) }),
        })
      );
    }

    return combine(...cleanups);
  }, [column.id, isDraggable]);

  return (
    <Box sx={{ position: "relative" }}>
      {dropIndicatorEdge === "top" && (
        <Box sx={{ position: "absolute", top: -4, left: 8, right: 8, height: 2, bgcolor: theme.palette.primary.main, borderRadius: 1 }} />
      )}
      <Box
        ref={rowRef}
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: theme.palette.secondary.grey,
          borderRadius: "999px",
          padding: "6px 10px",
          opacity: isDragging ? 0.4 : 1,
          transition: "opacity 0.15s ease",
        }}
      >
        {isDraggable ? (
          <Box
            ref={handleRef}
            sx={{
              cursor: "grab",
              display: "flex",
              alignItems: "center",
              color: theme.palette.secondary.black,
              opacity: 0.5,
              mr: "6px",
              "&:hover": { opacity: 0.8 },
              "&:active": { cursor: "grabbing" },
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: "16px" }} />
          </Box>
        ) : (
          <Box sx={{ width: "22px" }} />
        )}
        {children}
      </Box>
      {dropIndicatorEdge === "bottom" && (
        <Box sx={{ position: "absolute", bottom: -4, left: 8, right: 8, height: 2, bgcolor: theme.palette.primary.main, borderRadius: 1 }} />
      )}
    </Box>
  );
};

const ManageBoardPanel = ({ columns, cardsByColumnId, setColumns, setCardsByColumnId, onBoardMutated }) => {
  const intl = useIntl();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const { enqueueSnackbar } = useSnackbar();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [newName, setNewName] = useState("");
  const [newNameError, setNewNameError] = useState("");
  const [busy, setBusy] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState(null);
  const [draggingColumnId, setDraggingColumnId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { columnId, edge }

  const columnsRef = useRef(columns);
  useEffect(() => { columnsRef.current = columns; }, [columns]);

  const orderedColumns = useMemo(() => {
    return sortColumnsByPosition(columns);
  }, [columns]);

  const canAdd = orderedColumns.length < MAX_COLUMNS;
  const normalizedColumnNames = useMemo(
    () => new Set((orderedColumns || []).map((c) => (c?.name || "").trim().toLowerCase()).filter(Boolean)),
    [orderedColumns]
  );

  const countsById = useMemo(() => {
    const acc = {};
    (orderedColumns || []).forEach((c) => {
      const k = String(c.id);
      acc[c.id] = (cardsByColumnId?.[k] || []).length;
    });
    return acc;
  }, [orderedColumns, cardsByColumnId]);

  const normalizeCardsByColumns = (cols, prev) => {
    const next = { ...(prev || {}) };
    (cols || []).forEach((c) => {
      const k = String(c.id);
      if (!Array.isArray(next[k])) next[k] = [];
    });
    return next;
  };

  const refreshColumns = async () => {
    const res = await sh.get("/kanban_column");
    const cols = sortColumnsByPosition(res?.data?.data || []);
    setColumns(cols);
    setCardsByColumnId((prev) => normalizeCardsByColumns(cols, prev));
    return cols;
  };

  const addStep = async () => {
    const name = newName.trim();
    if (!name || !canAdd || busy) return;

    if (normalizedColumnNames.has(name.toLowerCase())) {
      setNewNameError(intl.formatMessage({
        id: "recruiter.kanban.column_name_exists",
        defaultMessage: "A column with this name already exists.",
      }));
      return;
    }

    setNewNameError("");
    setBusy(true);

    try {
      await sh.post("/kanban_column", {
        name,
        position: orderedColumns.length + 1,
        authenticity_token: authenticityToken,
      });

      await refreshColumns();
      setNewName("");
      setNewNameError("");
      if (typeof onBoardMutated === "function") onBoardMutated();
    } catch (e) {
      console.error(e);
      const apiErrors = e?.response?.data?.errors;
      const duplicateMessage = Array.isArray(apiErrors)
        ? apiErrors.find((msg) => /already been taken/i.test(String(msg)))
        : null;

      if (duplicateMessage) {
        setNewNameError(intl.formatMessage({
          id: "recruiter.kanban.column_name_exists",
          defaultMessage: "A column with this name already exists.",
        }));
      } else {
        setNewNameError("");
      }
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.column_add_error" }), { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const deleteColumn = async (col) => {
    if (!col?.id || busy) return;
    if (protectedKeys.has(col.key)) return;
    if ((countsById[col.id] || 0) > 0) return;

    setBusy(true);
    try {
      await sh.delete(`/kanban_column/${col.id}`, {
        data: { authenticity_token: authenticityToken },
      });

      setColumns((prev) => (prev || []).filter((c) => c.id !== col.id));
      setCardsByColumnId((prev) => {
        const next = { ...(prev || {}) };
        delete next[String(col.id)];
        return next;
      });
      await refreshColumns();
      if (typeof onBoardMutated === "function") onBoardMutated();
    } catch (e) {
      console.error(e);
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.error_deleting_column" }), { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const openDeleteConfirm = (col) => {
    if (!col?.id || busy) return;
    if (protectedKeys.has(col.key)) return;
    if ((countsById[col.id] || 0) > 0) return;

    setColumnToDelete(col);
    setIsConfirmModalOpen(true);
  };

  const handleColumnDrop = useCallback(
    async ({ source, location }) => {
      const sourceData = source?.data || {};
      if (sourceData.type !== DRAG_TYPE) return;

      const draggedColumnId = sourceData.columnId;
      const currentColumns = sortColumnsByPosition(columnsRef.current || []);
      const draggedColumn = currentColumns.find((c) => c.id === draggedColumnId);
      if (!draggedColumn || protectedKeys.has(draggedColumn.key)) return;

      const dropTargets = location?.current?.dropTargets || [];
      const columnTarget = dropTargets.find((t) => t?.data?.type === DROP_TYPE);
      if (!columnTarget) return;

      const targetColumnId = columnTarget.data.columnId;
      const edge = extractClosestEdge(columnTarget.data);
      const targetColumn = currentColumns.find((c) => c.id === targetColumnId);
      if (!targetColumn) return;

      const fromIndex = currentColumns.findIndex((c) => c.id === draggedColumnId);
      const targetIndex = currentColumns.findIndex((c) => c.id === targetColumnId);
      if (fromIndex === -1 || targetIndex === -1) return;

      let toIndex = getReorderDestinationIndex({
        startIndex: fromIndex,
        indexOfTarget: targetIndex,
        closestEdgeOfTarget: edge,
        axis: "vertical",
      });

      const pendingIndex = currentColumns.findIndex((c) => c.key === "pending");
      const hiredIndex = currentColumns.findIndex((c) => c.key === "hired");
      if (pendingIndex !== -1 && toIndex <= pendingIndex) toIndex = pendingIndex + 1;
      if (hiredIndex !== -1 && toIndex >= hiredIndex) toIndex = hiredIndex - 1;

      if (toIndex === fromIndex) return;

      const prevColumns = [...currentColumns];

      setColumns((prev) => {
        const sorted = sortColumnsByPosition(prev || []);
        const next = reorder({ list: [...sorted], startIndex: fromIndex, finishIndex: toIndex });
        return next.map((col, idx) => ({ ...col, position: idx + 1 }));
      });

      try {
        await sh.put(`/kanban_column/${draggedColumnId}`, {
          position: toIndex + 1,
          authenticity_token: authenticityToken,
        });
        if (typeof onBoardMutated === "function") onBoardMutated();
      } catch (e) {
        console.error(e);
        setColumns(prevColumns);
        enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.column_move_error" }), { variant: "error" });
      }
    },
    [authenticityToken, setColumns, enqueueSnackbar, intl, onBoardMutated]
  );

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => source?.data?.type === DRAG_TYPE,
      onDragStart: ({ source }) => setDraggingColumnId(source?.data?.columnId || null),
      onDrag: ({ location }) => {
        const dropTargets = location?.current?.dropTargets || [];
        const columnTarget = dropTargets.find((t) => t?.data?.type === DROP_TYPE);
        if (columnTarget) {
          const edge = extractClosestEdge(columnTarget.data);
          setDropTarget({ columnId: columnTarget.data.columnId, edge });
        } else {
          setDropTarget(null);
        }
      },
      onDrop: (args) => {
        setDraggingColumnId(null);
        setDropTarget(null);
        handleColumnDrop(args);
      },
    });
  }, [handleColumnDrop]);

  
  return (
    <Box
      sx={{
        backgroundColor: "#FFF",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.background.neutral}`,
        padding: "12px",
        width: isDesktop ? "380px" : "100%",
        maxWidth: "100%",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1 }}>
        <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif", fontSize: "14px", color: theme.palette.secondary.black }}>
          <FormattedMessage id="recruiter.kanban.board" />
        </Typography>
        <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif", fontSize: "12px", color: theme.palette.secondary.black, opacity: 0.7 }}>
          {orderedColumns.length}/{MAX_COLUMNS}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {orderedColumns.map((c) => {
          const count = countsById[c.id] || 0;
          const notProtectedKey = !protectedKeys.has(c.key);
          const canDelete = notProtectedKey && count === 0;
          const isDraggable = notProtectedKey && orderedColumns.length > 3;
          const isDragging = draggingColumnId === c.id;
          const dropIndicatorEdge = dropTarget?.columnId === c.id ? dropTarget.edge : null;

          return (
            <DraggableColumnRow
              key={c.id}
              column={c}
              isDraggable={isDraggable}
              isDragging={isDragging}
              dropIndicatorEdge={dropIndicatorEdge}
            >
              <Typography sx={{ flex: 1, fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif", color: theme.palette.secondary.black, opacity: 0.85 }} noWrap>
                {c.name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Semibold, sans-serif", color: theme.palette.secondary.black, opacity: 0.85 }}>
                  {String(count).padStart(2, "0")}
                </Typography>
                {notProtectedKey ? (
                  canDelete ? (
                    <IconButton size="small" disabled={busy} onClick={() => openDeleteConfirm(c)} sx={{ p: "2px" }}>
                      <CloseIcon sx={{ fontSize: "16px", color: theme.palette.secondary.black, opacity: 0.7 }} />
                    </IconButton>
                  ) : 
                    <Tooltip title={intl.formatMessage({ id: "recruiter.kanban.error_deleting_column" })} placement="top">
                      <IconButton size="small" disabled={busy} onClick={() => deleteColumn(c)} sx={{ p: "2px" }} disableRipple disableFocusRipple>
                        <CloseIcon sx={{ fontSize: "16px", color: theme.palette.secondary.black, opacity: 0.7, cursor: "not-allowed" }} />
                      </IconButton>
                    </Tooltip>
                ) : (
                  <Tooltip title={intl.formatMessage({ id: "recruiter.kanban.protected_column" })} placement="top">
                    <IconButton size="small" disabled={busy} onClick={() => deleteColumn(c)} sx={{ p: "2px" }} disableRipple disableFocusRipple>
                      <LockIcon sx={{ fontSize: "16px", color: theme.palette.secondary.black, opacity: 0.7, cursor: "not-allowed" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </DraggableColumnRow>
          );
        })}
      </Box>

      {canAdd ? (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "4px" }}>
            <Tooltip
              title={intl.formatMessage({
                id: "recruiter.kanban.stage_visible_to_candidates",
                defaultMessage: "Candidates can see this stage name in their application dashboard.",
              })}
              placement="top"
            >
              <InfoOutlinedIcon sx={{ fontSize: "14px", color: theme.palette.secondary.black, opacity: 0.5, cursor: "help" }} />
            </Tooltip>
            <Typography sx={{ fontSize: "11px", color: theme.palette.secondary.black, opacity: 0.5 }}>
              <FormattedMessage
                id="recruiter.kanban.stage_visible_to_candidates"
                defaultMessage="Candidates can see this stage name in their application dashboard."
              />
            </Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            disabled={busy}
            value={newName}
            error={Boolean(newNameError)}
            helperText={newNameError || " "}
            onChange={(e) => {
              setNewName(e.target.value);
              if (newNameError) setNewNameError("");
            }}
            placeholder={intl.formatMessage({ id: "recruiter.kanban.add_step" })}
            onKeyDown={(e) => {
              if (e.key === "Enter") addStep();
            }}
            InputProps={{
              sx: { borderRadius: "999px", height: "36px", backgroundColor: "#FFF" },
            }}
          />
        </Box>
      ) : null}

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        setIsOpen={setIsConfirmModalOpen}
        handleConfirm={() => deleteColumn(columnToDelete)}
        labelId="delete_column_confirmation"
      />
    </Box>
  );
};

export default ManageBoardPanel;


